import { RefObject, useCallback, useEffect, useRef } from 'react';
import flatpickr from 'flatpickr';
import { CreateFlatpickrFooter, createFooterButtons } from '@/datetime/flatpickr/flatpickrButtons.util';
import { useTranslation } from 'react-i18next';
import { positionFlatpickrCalendar } from '@/datetime/flatpickr/flatpickrPositioning.util';
import { useEffectOnceWhen } from 'rooks';
import {
  areFormattedDatesEqual,
  momentToTimezoneNaiveDate,
  timezoneNaiveDateToMoment,
} from '@/datetime/flatpickr/flatpickrTimezones.util';
import type { Timezone } from '@/datetime/timezone.service';
import type { Moment } from 'moment-timezone';
import type { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';

export interface UseFlatpickrParameters {
  /**
   * Input element to attach flatpickr to
   */
  anchorRef: React.RefObject<HTMLElement | null>;

  /**
   * Current datetime value
   */
  value: Moment | null | undefined;

  /**
   * The tz to be used for datetime value
   */
  timezone: Timezone;

  /**
   * Format string used to display the datetime value
   */
  localizedDateFormat: string;

  /**
   * Callback to handle date selection
   * @param date The selected date
   */
  onDateSelect: (date: Moment | undefined) => void;

  /**
   * Callback to handle manual input
   * @param input The input value
   * @returns The parsed date or undefined if parsing fails
   */
  onManualInput?: (input: string) => Moment | undefined;

  /**
   * Flag to enable the seconds input
   */
  enableSeconds?: boolean;

  /**
   * Flag to enable the time input
   */
  enableTime?: boolean;

  /**
   * Flag to display time in 24-hour format
   */
  enableTime24hr?: boolean;

  /**
   * Flag to indicate if the picker is used on a modal
   */
  isInsideModal?: boolean;

  /**
   * The minimum date that a user can start picking from (inclusive).
   */
  minDate?: Moment;

  /**
   * The maximum date that a user can pick to (inclusive).
   */
  maxDate?: Moment;

  /**
   * Adjusts the step for the minute input
   */
  minuteIncrement?: number;

  /**
   * Flag to render the picker inline
   */
  inline?: boolean;

  /**
   * Flag to control whether the flatpickr is open. If undefined, the picker will be open by default.
   */
  isOpen?: boolean;

  /**
   * Function to render the footer of the flatpickr instance.
   */
  renderFooter?: CreateFlatpickrFooter;
}

export const useFlatpickr = ({
  anchorRef,
  value,
  timezone,
  localizedDateFormat,
  enableSeconds,
  enableTime,
  enableTime24hr,
  onDateSelect,
  onManualInput,
  isInsideModal,
  minDate,
  maxDate,
  minuteIncrement,
  inline,
  isOpen,
  renderFooter,
}: UseFlatpickrParameters): RefObject<FlatpickrInstance | null> => {
  const { t } = useTranslation();
  const flatpickrRef = useRef<FlatpickrInstance | null>(null);
  const buttonCleanupRef = useRef<(() => void) | null>(null);

  const formatDate = useCallback(
    (date: Date, _format?: string): string => {
      const targetMoment = timezoneNaiveDateToMoment(date, timezone);
      return targetMoment.format(localizedDateFormat);
    },
    [timezone, localizedDateFormat],
  );

  const parseDate = useCallback(
    (dateString: string, _format?: string, _timeless?: boolean): Date => {
      const parsedMoment = onManualInput?.(dateString);
      if (!parsedMoment) {
        // Return a default date if parsing fails
        return new Date();
      }
      const timezoneNaiveDate = momentToTimezoneNaiveDate(parsedMoment, timezone);
      flatpickrRef.current?.setDate(timezoneNaiveDate, false);
      return timezoneNaiveDate;
    },
    [onManualInput, timezone],
  );

  const handleReady = useCallback(
    (_dates: Date[], _dateStr: string, instance: FlatpickrInstance) => {
      if (typeof renderFooter === 'function') {
        renderFooter({
          flatpickrInstance: instance,
        });
        return;
      }
      const onCancel = () => {
        const resetDate = value ? momentToTimezoneNaiveDate(value, timezone) : undefined;
        if (resetDate) {
          instance.setDate(resetDate, false);
        } else {
          instance.clear();
        }
        instance.close();
      };
      buttonCleanupRef.current = createFooterButtons({ flatpickrInstance: instance, t, onCancel });
    },
    [renderFooter, t, value?.valueOf(), timezone],
  );

  const handleDateChange = useCallback(
    (selectedDates: Date[]) => {
      if (selectedDates.length > 0) {
        const selectedDate = selectedDates[0];
        const zonedDate = timezoneNaiveDateToMoment(selectedDate, timezone);
        onDateSelect(zonedDate);
      } else {
        // No dates selected - clear the date
        onDateSelect(undefined);
      }
    },
    [onDateSelect, timezone],
  );

  const getModalContainer = useCallback((): HTMLElement | null => {
    return (anchorRef.current?.closest('[data-state="open"][role="dialog"]') as HTMLElement) || null;
  }, [anchorRef]);

  /**
   * Sync flatpickr display when value or timezone changes
   *
   * This ensures the input field shows the correct formatted time
   * when props change externally (e.g., timezone switches, programmatic date updates).
   */
  useEffect(() => {
    if (flatpickrRef.current) {
      if (value) {
        const currentlyDisplayedValue = flatpickrRef.current.input.value;
        const needsSynced = !areFormattedDatesEqual(currentlyDisplayedValue, value, timezone, localizedDateFormat);
        if (needsSynced) {
          const jsDate = momentToTimezoneNaiveDate(value, timezone);
          flatpickrRef.current.setDate(jsDate, false);
        }
      } else {
        flatpickrRef.current.clear();
      }
    }
  }, [value?.valueOf(), timezone, localizedDateFormat]);

  useEffect(() => {
    if (flatpickrRef.current) {
      flatpickrRef.current.config.parseDate = parseDate;
      flatpickrRef.current.config.onChange = [handleDateChange];
      flatpickrRef.current.config.formatDate = formatDate;
    }
  }, [parseDate, handleDateChange, formatDate]);

  useEffect(() => {
    if (flatpickrRef.current && isOpen !== undefined) {
      if (isOpen) {
        flatpickrRef.current.open();
      } else {
        flatpickrRef.current.close();
      }
    }
  }, [isOpen]);

  useEffectOnceWhen(() => {
    if (!anchorRef.current) return;

    const appendTo = isInsideModal ? getModalContainer() : null;
    const initialDate = value ? momentToTimezoneNaiveDate(value, timezone) : undefined;

    flatpickrRef.current = flatpickr(anchorRef.current, {
      ...(!!appendTo && {
        appendTo,
        position: (fp: FlatpickrInstance, relativeEl: HTMLElement | undefined) => {
          positionFlatpickrCalendar(fp, relativeEl, appendTo);
        },
      }),
      allowInput: true,
      clickOpens: true,
      closeOnSelect: true,
      dateFormat: 'Z', // Use ISO format internally
      defaultDate: initialDate,
      disableMobile: true,
      enableSeconds,
      enableTime,
      formatDate,
      parseDate,
      time_24hr: enableTime24hr,
      inline,
      onChange: handleDateChange,
      onClose: () => {
        anchorRef.current?.blur();
      },
      onReady: handleReady,
      ...(minDate && { minDate: momentToTimezoneNaiveDate(minDate, timezone) }),
      ...(maxDate && { maxDate: momentToTimezoneNaiveDate(maxDate, timezone) }),
      ...(typeof minuteIncrement === 'number' && { minuteIncrement }),
    });

    return () => {
      buttonCleanupRef.current?.();
      buttonCleanupRef.current = null;
      flatpickrRef.current?.destroy();
      flatpickrRef.current = null;
    };
  }, !!anchorRef.current);

  return flatpickrRef;
};
